import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  type User,
  type AuthError,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth } from '@/firebase';
import { setSentryUser } from '@/config/sentry';
import { identifyUser, resetAnalytics } from '@/config/analytics';
import { loginRevenueCat, logoutRevenueCat } from '@/config/revenuecat';

// ── Error mapping ──

const googleProvider = new GoogleAuthProvider();

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

// TWA and installed PWAs run in standalone display mode — popups are blocked,
// so we use redirect-based sign-in instead.
function isStandaloneMode(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches;
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// Popup couldn't even open — safe to fall back to redirect. Deliberately excludes
// auth/popup-closed-by-user, which means the user cancelled on purpose.
const POPUP_UNAVAILABLE = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
]);

/**
 * Browser sign-in for a given provider.
 *
 * On iOS the redirect flow is broken inside an installed PWA: the OAuth round
 * trip runs in a separate browser context with its own storage partition, and
 * iOS then cold-relaunches the app at start_url — dropping both the credential
 * fragment and the pendingRedirect marker, so getRedirectResult() returns null
 * and the user lands back on the logged-out screen. The popup keeps the whole
 * exchange inside the live PWA context, so try it first there and only fall
 * back to redirect if the popup could not open at all.
 */
async function startWebSignIn(provider: GoogleAuthProvider | OAuthProvider): Promise<void> {
  if (!isStandaloneMode()) {
    await signInWithPopup(auth, provider);
    return;
  }
  if (isIOS()) {
    try {
      await signInWithPopup(auth, provider);
      return;
    } catch (e) {
      if (!POPUP_UNAVAILABLE.has((e as AuthError).code)) throw e;
    }
  }
  await signInWithRedirect(auth, provider);
}

function mapAuthError(error: AuthError): string | null {
  switch (error.code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect password';
    case 'auth/user-not-found':
      return 'No account found with this email';
    case 'auth/popup-closed-by-user':
      return null;
    case 'auth/invalid-email':
      return 'Please enter a valid email address';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection';
    default:
      return 'Something went wrong. Please try again';
  }
}

// ── Context shape ──

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  continueWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// ── Provider ──

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Process the result of a redirect-based Google sign-in (TWA / standalone PWA).
    // This is a no-op in regular browser sessions.
    getRedirectResult(auth).catch(e => {
      const msg = mapAuthError(e as AuthError);
      if (msg) setError(msg);
    });

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setSentryUser(firebaseUser?.uid ?? null);
      if (firebaseUser) {
        identifyUser(firebaseUser.uid);
        // No-op on web/until RevenueCat is linked — see config/revenuecat.ts.
        // Must run before any purchase attempt, which is why it's tied to
        // auth state rather than to opening a purchase screen.
        void loginRevenueCat(firebaseUser.uid);
      } else {
        resetAnalytics();
        void logoutRevenueCat();
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      if (Capacitor.isNativePlatform()) {
        // Native Android/iOS: use Capacitor plugin for the Google account picker,
        // then manually pass the credential to the Firebase web SDK so that
        // onAuthStateChanged fires and the rest of the app sees the signed-in user.
        const { credential } = await FirebaseAuthentication.signInWithGoogle();
        if (!credential?.idToken) throw new Error('Google sign-in returned no credential');
        await signInWithCredential(auth, GoogleAuthProvider.credential(credential.idToken));
      } else {
        await startWebSignIn(googleProvider);
      }
    } catch (e) {
      console.error('[Auth] signInWithGoogle error:', e);
      const msg = mapAuthError(e as AuthError);
      if (msg) setError(msg);
    }
  }, []);

  const signInWithApple = useCallback(async () => {
    setError(null);
    try {
      if (Capacitor.isNativePlatform()) {
        // Native: same pattern as Google — use Capacitor plugin then sync to web SDK
        const { credential } = await FirebaseAuthentication.signInWithApple();
        if (!credential?.idToken) throw new Error('Apple sign-in returned no credential');
        const oauthCredential = new OAuthProvider('apple.com').credential({
          idToken: credential.idToken,
          rawNonce: credential.nonce,
        });
        await signInWithCredential(auth, oauthCredential);
      } else {
        await startWebSignIn(appleProvider);
      }
    } catch (e) {
      console.error('[Auth] signInWithApple error:', e);
      const msg = mapAuthError(e as AuthError);
      if (msg) setError(msg);
    }
  }, []);

  const continueWithEmail = useCallback(async (email: string, password: string, displayName?: string) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (signInErr) {
      const code = (signInErr as AuthError).code;
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        // User may not exist — attempt sign-up
        try {
          const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
          if (displayName) await updateProfile(newUser, { displayName });
        } catch (signUpErr) {
          const signUpCode = (signUpErr as AuthError).code;
          if (signUpCode === 'auth/email-already-in-use') {
            // Account exists — the original sign-in failed due to wrong password
            setError('Incorrect password');
          } else {
            const msg = mapAuthError(signUpErr as AuthError);
            if (msg) setError(msg);
          }
        }
      } else {
        const msg = mapAuthError(signInErr as AuthError);
        if (msg) setError(msg);
      }
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      if (Capacitor.isNativePlatform()) {
        // Best-effort: clear the native provider layer (Google account picker
        // cache). The real session lives in the web SDK — under skipNativeAuth
        // the plugin never holds a user, so this alone signs nothing out.
        await FirebaseAuthentication.signOut().catch(() => {});
      }
      await firebaseSignOut(auth);
    } catch (e) {
      const msg = mapAuthError(e as AuthError);
      if (msg) setError(msg);
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    error,
    signInWithGoogle,
    signInWithApple,
    continueWithEmail,
    signOut,
  }), [user, loading, error, signInWithGoogle, signInWithApple, continueWithEmail, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
